import {shallowEqual} from 'react-redux';

import {useRumGlobalContext} from '../../util/datadog/datadogRum';
import {useActiveThread, useStoreSelector} from '../chat/store/context';
import {
  selectHasRunningThread,
  selectRunningThreadIds,
  selectRunningTurnIds,
} from '../chat/store/selectors';
import {useAgentChatAvailability} from './useAgentChatAvailability';
import {
  useSelectedThreadId,
  useWBAgentPresentationController,
} from './WBAgentPresentationController';

/**
 * Publishes every `aria.*` global RUM context key: surface state, running
 * state, and the ids of the thread on screen and of every running thread, so a
 * session can be found from a thread or turn id. Must render inside
 * `WBAgentPresentationControllerProvider`. See `util/datadog/README.md`.
 */
export function useAriaRumContext(): void {
  const {displayMode, isOpen} = useWBAgentPresentationController();
  const {isAvailable} = useAgentChatAvailability();
  const isOnScreen = isAvailable && isOpen;
  const selectedThreadId = useSelectedThreadId();
  const activeThread = useActiveThread();
  const threadId = isOnScreen ? selectedThreadId : null;
  const turnId = threadId != null ? (activeThread?.latestTurnId ?? null) : null;
  const hasRunningThread = useStoreSelector(selectHasRunningThread);
  const runningThreadIds = useStoreSelector(
    selectRunningThreadIds,
    shallowEqual
  );
  const runningTurnIds = useStoreSelector(selectRunningTurnIds, shallowEqual);

  useRumGlobalContext('aria.thread_id', threadId, threadId != null);
  useRumGlobalContext('aria.turn_id', turnId, turnId != null);
  useRumGlobalContext(
    'aria.running_thread_ids',
    runningThreadIds,
    runningThreadIds.length > 0
  );
  useRumGlobalContext(
    'aria.running_turn_ids',
    runningTurnIds,
    runningTurnIds.length > 0
  );
  useRumGlobalContext('aria.is_running', hasRunningThread);
  useRumGlobalContext(
    'aria.sidebar_open',
    isOnScreen && displayMode !== 'page'
  );
}
